// Member-initiated bug report. The top-bar Send feedback dialog submits the
// description, plus any screenshots already staged through the shared post-
// media presign, to `POST /api/v1/bug-reports`, which files a GitHub issue via
// the App credentials in the server's deps. Errors surface as `DomainError`
// (via `unwrap`) carrying the server's code — the caller owns the copy.

import { useMutation } from '@tanstack/react-query';
import type { components } from '@leanin/api-client';
import { getApiClient } from '../api-client';
import { unwrap } from '../api-error';

export type BugReportScreenshot = components['schemas']['BugReportScreenshot'];

export interface CreateBugReportInput {
  description: string;
  screenshots: readonly BugReportScreenshot[];
}

export interface CreateBugReportResult {
  issueNumber: number;
  /** Browser-facing URL to the resulting GitHub issue. Only useful for
   *  staff with repo access; the reporter sees a generic confirmation. */
  issueUrl: string;
}

/** Returns a `WxH` string from the current viewport, or null when the
 *  app is rendered in a non-browser context. Folded into the report
 *  via the `X-Viewport` request header. */
function readViewport(): string | null {
  if (typeof window === 'undefined') return null;
  const w = Math.round(window.innerWidth);
  const h = Math.round(window.innerHeight);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;
  return `${w}x${h}`;
}

export function useCreateBugReport() {
  return useMutation<CreateBugReportResult, Error, CreateBugReportInput>({
    mutationFn: async (input) => {
      const client = getApiClient();
      const headers: Record<string, string> = {};
      const viewport = readViewport();
      if (viewport !== null) headers['X-Viewport'] = viewport;
      return unwrap(
        await client.POST('/api/v1/bug-reports', {
          body: { description: input.description, screenshots: [...input.screenshots] },
          headers,
        }),
      );
    },
  });
}
